import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_CONFIG } from '../../config';

interface ProductImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  isMain: boolean;
  order: number;
}

interface ProductVariant {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  price: number;
  discountPercentage: number;
  finalPrice: number;
  availableQuantity: number;
  isOutOfStock: boolean;
  order: number;
  isDefault: boolean;
  optionValues: { optionGroupId: string; optionGroupName: string; optionValueId: string; optionValueName: string }[];
}

interface ProductOptionValue {
  id: string;
  name: string;
  order: number;
  priceAdjustment: number;
}

interface ProductOptionGroup {
  id: string;
  name: string;
  order: number;
  values: ProductOptionValue[];
}

interface StorefrontProduct {
  id: string;
  name: string;
  description: string;
  categoryId?: string;
  categoryName?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  images: ProductImage[];
  variants: ProductVariant[];
  optionGroups: ProductOptionGroup[];
  minPrice?: number;
  maxPrice?: number;
  isFeatured: boolean;
  isBestSeller: boolean;
  isOutOfStock: boolean;
  totalAvailableQuantity: number;
  defaultVariantId?: string;
  createdAt?: string;
}

interface ProductsResponse {
  data: StorefrontProduct[];
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

interface ProductQueryParams {
  pageNumber?: number;
  pageSize?: number;
  categoryId?: string;
  subcategoryId?: string;
}

export const storefrontProductsApi = createApi({
  reducerPath: 'storefrontProductsApi',
  baseQuery: fetchBaseQuery({ baseUrl: API_CONFIG.API_URL }),
  tagTypes: ['StorefrontProducts'],
  // Retry failed requests up to 2 times with exponential backoff
  refetchOnMountOrArgChange: 30,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getFeaturedProducts: builder.query<StorefrontProduct[], void>({
      query: () => '/product/featured',
      transformResponse: (response: any) => {
        const data = response?.result?.data || response?.data || response?.result || [];
        return Array.isArray(data) ? data : [];
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getBestSellerProducts: builder.query<StorefrontProduct[], void>({
      query: () => '/product/best-sellers',
      transformResponse: (response: any) => {
        const data = response?.result?.data || response?.data || response?.result || [];
        return Array.isArray(data) ? data : [];
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getOnSaleProducts: builder.query<StorefrontProduct[], void>({
      query: () => '/product/on-sale',
      transformResponse: (response: any) => {
        const data = response?.result?.data || response?.data || response?.result || [];
        return Array.isArray(data) ? data.filter(
          (product: StorefrontProduct) => product.variants?.some((v: ProductVariant) => v.discountPercentage > 0)
        ) : [];
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getProductsByCategory: builder.query<StorefrontProduct[], { categoryId: string; searchTerm?: string; sortBy?: string }>({
      query: ({ categoryId, searchTerm, sortBy }) => {
        const params = new URLSearchParams();
        if (searchTerm) params.append('searchTerm', searchTerm);
        if (sortBy) params.append('sortBy', sortBy);
        const queryString = params.toString();
        return `/product/category/${categoryId}${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => {
        const data = response?.result || response?.data || [];
        return Array.isArray(data) ? data : [];
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getProductsBySubcategory: builder.query<StorefrontProduct[], { subcategoryId: string; searchTerm?: string; sortBy?: string }>({
      query: ({ subcategoryId, searchTerm, sortBy }) => {
        const params = new URLSearchParams();
        if (searchTerm) params.append('searchTerm', searchTerm);
        if (sortBy) params.append('sortBy', sortBy);
        const queryString = params.toString();
        return `/product/subcategory/${subcategoryId}${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: any) => {
        const data = response?.result || response?.data || [];
        return Array.isArray(data) ? data : [];
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getProductById: builder.query<StorefrontProduct, string>({
      query: (id) => `/product/${id}`,
      transformResponse: (response: any) => {
        // Handle different response structures - API returns { result: productDto }
        return response?.result || response?.data || response;
      },
      providesTags: ['StorefrontProducts'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      // Retry failed requests
      extraOptions: { maxRetries: 2 },
    }),

    getRelatedProducts: builder.query<StorefrontProduct[], { productId: string; limit?: number }>({
      query: ({ productId, limit = 10 }) => `/product/${productId}/related?limit=${limit}`,
      transformResponse: (response: any) => {
        const data = response?.result?.data || response?.data || [];
        return Array.isArray(data) ? data : [];
      },
      providesTags: ['StorefrontProducts'],
      keepUnusedDataFor: 300,
      extraOptions: { maxRetries: 2 },
    }),

    searchProducts: builder.query<{ data: StorefrontProduct[]; totalCount: number }, { searchTerm: string; sortBy?: string; pageNumber?: number; pageSize?: number }>({
      query: ({ searchTerm, sortBy, pageNumber = 1, pageSize = 20 }) => {
        const params = new URLSearchParams();
        params.append('searchTerm', searchTerm);
        params.append('pageNumber', String(pageNumber));
        params.append('pageSize', String(pageSize));
        if (sortBy) params.append('sortBy', sortBy);
        return `/product?${params.toString()}`;
      },
      transformResponse: (response: any) => {
        const result = response?.result || response;
        const data = result?.data || [];
        const totalCount = result?.pagination?.totalCount || data.length;
        return { data: Array.isArray(data) ? data : [], totalCount };
      },
      providesTags: ['StorefrontProducts'],
      keepUnusedDataFor: 300,
      extraOptions: { maxRetries: 2 },
    }),
  }),
});

export const {
  useGetFeaturedProductsQuery,
  useGetBestSellerProductsQuery,
  useGetOnSaleProductsQuery,
  useGetProductsByCategoryQuery,
  useGetProductsBySubcategoryQuery,
  useGetProductByIdQuery,
  useGetRelatedProductsQuery,
  useSearchProductsQuery,
} = storefrontProductsApi;
